import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Loader2, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  project_type: "",
  project_location: "",
  message: "",
};

const inputClass =
  "w-full bg-white border border-zinc-200 rounded-sm px-4 py-3.5 text-sm text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-950 transition-colors duration-300";

export default function ContactSection() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.project_type) {
      toast.error("Please select a project type.");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiries`, form);
      toast.success(res.data.message || "Enquiry sent successfully.");
      setForm(initial);
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Could not send your enquiry. Please email us directly at srbh9549@gmail.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 bg-white border-t border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <Reveal>
        <div>
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-zinc-500 mb-5">06 &middot; Contact</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-zinc-950 leading-tight">
            Let&apos;s Build Something Great
          </h2>
          <p className="mt-6 text-base leading-relaxed text-zinc-600 max-w-md">
            Tell us about your project and we will get back to you with a clear,
            straightforward plan and estimate.
          </p>

          <div className="mt-12 space-y-6">
            <div>
              <h3 className="font-heading text-lg font-medium text-zinc-950">Saurabh Decor Creations</h3>
              <p className="mt-2 flex items-center gap-2.5 text-sm text-zinc-600">
                <MapPin className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                Mumbai, Maharashtra, India
              </p>
              <a
                data-testid="contact-email-link"
                href="mailto:srbh9549@gmail.com"
                className="mt-2 flex items-center gap-2.5 text-sm text-zinc-600 hover:text-zinc-950 transition-colors duration-300"
              >
                <Mail className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                srbh9549@gmail.com
              </a>
              <a
                data-testid="contact-phone-link"
                href="tel:+918850727221"
                className="mt-2 flex items-center gap-2.5 text-sm text-zinc-600 hover:text-zinc-950 transition-colors duration-300"
              >
                <Phone className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                +91 88507 27221
              </a>
            </div>
            <div className="border-t border-zinc-200 pt-6">
              <p className="text-sm text-zinc-500 leading-relaxed">
                Civil Work &bull; Interior Work &bull; Modular Kitchen &bull; Restoration &amp; Renovation
              </p>
            </div>
          </div>
        </div>
        </Reveal>

        <form data-testid="enquiry-form" onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="name" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Name</label>
              <input data-testid="enquiry-name-input" id="name" name="name" required value={form.name} onChange={update} placeholder="Your full name" className={inputClass} />
            </div>
            <div>
              <label htmlFor="phone" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Phone Number</label>
              <input data-testid="enquiry-phone-input" id="phone" name="phone" type="tel" required value={form.phone} onChange={update} placeholder="Your phone number" className={inputClass} />
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Email</label>
            <input data-testid="enquiry-email-input" id="email" name="email" type="email" required value={form.email} onChange={update} placeholder="you@example.com" className={inputClass} />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="project_type" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Project Type</label>
              <select data-testid="enquiry-project-type-select" id="project_type" name="project_type" required value={form.project_type} onChange={update} className={inputClass}>
                <option value="" disabled>Select project type</option>
                <option>Civil Work</option>
                <option>Interior Work</option>
                <option>Modular Kitchen</option>
                <option>Restoration &amp; Renovation</option>
              </select>
            </div>
            <div>
              <label htmlFor="project_location" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Project Location</label>
              <input data-testid="enquiry-location-input" id="project_location" name="project_location" required value={form.project_location} onChange={update} placeholder="e.g. Bandra West, Mumbai" className={inputClass} />
            </div>
          </div>
          <div>
            <label htmlFor="message" className="block text-xs font-semibold tracking-wide uppercase text-zinc-500 mb-2">Message</label>
            <textarea data-testid="enquiry-message-input" id="message" name="message" required rows={5} value={form.message} onChange={update} placeholder="Tell us about your project requirements" className={`${inputClass} resize-none`} />
          </div>
          <button
            data-testid="enquiry-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full bg-zinc-950 text-white text-sm font-semibold px-8 py-4 rounded-sm hover:bg-zinc-800 disabled:opacity-60 transition-colors duration-300 inline-flex items-center justify-center gap-2"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </form>
      </div>
    </section>
  );
}
